import { useEffect, useMemo, useState } from "react";
import { useTratamente } from "../../context/TratamenteContext";
import { useServices } from "../../context/ServicesContext";
import { usePresets } from "../../context/PresetsContext";
import { useUiPreferences } from "../../context/UiPreferencesContext";
import Button from "../ui/Button";
import {
  TREATMENT_SCOPES,
  getScopeLabel,
  normalizeScope,
  isServiceScopeAllowed,
} from "../../utils/treatmentScope";
import { rgbaFromHex } from "../../utils/colorUtils";

// Arcade dentare + grupuri de preț
const upperTeeth = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28];
const lowerTeeth = [48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38];
const groupA = [13,12,11,21,22,23,33,43];
const groupB = [14,15,24,25,34,35,44,45,31,41,32,42];
const groupC = [18,17,16,26,27,28,36,37,38,46,47,48];

const getGroup = (tooth) => {
  if (groupA.includes(Number(tooth))) return "A";
  if (groupB.includes(Number(tooth))) return "B";
  if (groupC.includes(Number(tooth))) return "C";
  return "A";
};

export default function Tratamente({ patientId, editingTooth, clearEditing }) {
  const { tratamente, addTratament, updateTratament } = useTratamente();
  const { services, loading: servicesLoading, error: servicesError } = useServices();
  const { presets } = usePresets();
  const { categoryColors } = useUiPreferences();
  const [selectedScope, setSelectedScope] = useState(TREATMENT_SCOPES.TOOTH);
  const [selectedTooth, setSelectedTooth] = useState("");
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [editingTratamentId, setEditingTratamentId] = useState(null);

  const categories = useMemo(() => {
    const map = new Map();
    (services || [])
      .filter((srv) => srv.active !== false)
      .filter((srv) => isServiceScopeAllowed(srv.scope, selectedScope))
      .forEach((srv) => {
        const categoryName = (srv.category || "Altele").trim() || "Altele";
        if (!map.has(categoryName)) {
          map.set(categoryName, []);
        }
        map.get(categoryName).push(srv);
      });
    return Array.from(map.entries());
  }, [services, selectedScope]);

  // populate dacă e editare
  useEffect(() => {
    if (editingTooth) {
      const services = Array.isArray(editingTooth.services)
        ? editingTooth.services
        : [];
      const normalizedScope = normalizeScope(editingTooth.scope, editingTooth.tooth);
      setSelectedScope(normalizedScope);
      setSelectedTooth(editingTooth.tooth || "");
      setSelectedTreatments(services.filter((srv) => !srv.done));
      setEditingTratamentId(editingTooth.id);
    }
  }, [editingTooth]);

  const resolveBasePrice = (service, tooth, scope) => {
    if (scope !== TREATMENT_SCOPES.TOOTH) {
      return Number(service?.basePrice || 0);
    }
    const group = getGroup(tooth);
    const priceByGroup = service?.meta?.priceByGroup;
    if (priceByGroup && typeof priceByGroup === "object") {
      const value = priceByGroup[group];
      if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
      }
    }
    return Number(service?.basePrice || 0);
  };

  const resolveCommissionTag = (service) => {
    const tag = service?.meta?.commissionTag;
    if (tag === "primary" || tag === "retreat") return tag;
    const name = (service?.name || "").toLowerCase();
    if (name.includes("retrat")) return "retreat";
    if (name.includes("primar")) return "primary";
    return "";
  };

  // toggle tratament
  const handleToggleTreatment = (cat, item) => {
    const exists = selectedTreatments.find((x) => {
      if (x.serviceId && item.id) return x.serviceId === item.id;
      return x.cat === cat && x.name === item.name;
    });
    if (exists) {
      setSelectedTreatments((prev) =>
        prev.filter((x) => {
          if (x.serviceId && item.id) return x.serviceId !== item.id;
          return !(x.cat === cat && x.name === item.name);
        })
      );
    } else {
      const basePrice = resolveBasePrice(item, selectedTooth, selectedScope);
      const discount = Number(item.defaultDiscount || 0);
      const finalPrice = Math.max(0, basePrice - (basePrice * discount) / 100);
      setSelectedTreatments((prev) => [
        ...prev,
        {
          serviceId: item.id || null,
          scope: selectedScope,
          cat,
          category: cat,
          name: item.name,
          basePrice,
          discount,
          finalPrice,
          commissionTag: resolveCommissionTag(item),
          done: false,
        },
      ]);
    }
  };

  const updateDiscount = (index, discount) => {
    setSelectedTreatments((prev) =>
      prev.map((t, i) =>
        i === index
          ? { ...t, discount, finalPrice: Math.max(0, t.basePrice - (t.basePrice * discount) / 100) }
          : t
      )
    );
  };

  const handleSaveTooth = async () => {
    const existingPlans = Array.isArray(tratamente[patientId])
      ? tratamente[patientId]
      : [];
    const duplicate = existingPlans.find((t) => {
      const scope = normalizeScope(t.scope, t.tooth);
      if (scope !== selectedScope) return false;
      if (scope === TREATMENT_SCOPES.TOOTH) {
        return t.tooth === selectedTooth;
      }
      return true;
    });
    const targetPlanId = editingTratamentId || duplicate?.id;
    const existingPlan = targetPlanId
      ? existingPlans.find((t) => t.id === targetPlanId)
      : null;
    const doneServices = existingPlan
      ? (existingPlan.services || []).filter((srv) => srv.done)
      : [];
    const servicesPayload = [
      ...doneServices.map((srv) => ({
        ...srv,
        scope: srv.scope || selectedScope,
        tooth: selectedScope === TREATMENT_SCOPES.TOOTH ? srv.tooth || selectedTooth : "",
      })),
      ...selectedTreatments.map((srv) => ({
        ...srv,
        serviceId: srv.serviceId || null,
        category: srv.category || srv.cat || "",
        commissionTag: srv.commissionTag || "",
        scope: selectedScope,
        tooth: selectedScope === TREATMENT_SCOPES.TOOTH ? selectedTooth : "",
        done: Boolean(srv.done),
      })),
    ];

    if ((selectedScope === TREATMENT_SCOPES.TOOTH && !selectedTooth) || servicesPayload.length === 0) {
      alert("Planul trebuie să conțină cel puțin un serviciu.");
      return;
    }

    try {
      if (editingTratamentId) {
        await updateTratament(patientId, editingTratamentId, {
          scope: selectedScope,
          tooth: selectedScope === TREATMENT_SCOPES.TOOTH ? selectedTooth : "",
          services: servicesPayload,
        });
        alert("Tratament actualizat!");
      } else if (duplicate) {
        await updateTratament(patientId, duplicate.id, {
          scope: selectedScope,
          tooth: selectedScope === TREATMENT_SCOPES.TOOTH ? selectedTooth : "",
          services: servicesPayload,
        });
        alert("Tratamentul existent pentru această zonă a fost actualizat!");
      } else {
        await addTratament(patientId, selectedTooth, servicesPayload, { scope: selectedScope });
        alert("Tratamente salvate!");
      }
    } catch (err) {
      console.error("Eroare salvare tratament:", err);
      alert("Nu am putut salva tratamentul. Încearcă din nou.");
      return;
    }

    setSelectedTooth("");
    setSelectedScope(TREATMENT_SCOPES.TOOTH);
    setSelectedTreatments([]);
    setEditingTratamentId(null);
    if (clearEditing) clearEditing();
  };

  const total = selectedTreatments.reduce((s, t) => s + t.finalPrice, 0);

  const activePresets = useMemo(
    () => (presets || []).filter((preset) => preset.active !== false),
    [presets]
  );

  const applyPreset = (preset) => {
    if (!preset) return;
    if (selectedScope === TREATMENT_SCOPES.TOOTH && !selectedTooth) return;
    const serviceMap = new Map((services || []).map((srv) => [srv.id, srv]));
    const additions = [];
    (preset.serviceIds || []).forEach((id) => {
      const service = serviceMap.get(id);
      if (!service) return;
      if (!isServiceScopeAllowed(service.scope, selectedScope)) return;
      const exists = selectedTreatments.some((item) => item.serviceId === service.id);
      if (exists) return;
      const basePrice = resolveBasePrice(service, selectedTooth, selectedScope);
      const discount = Number(service.defaultDiscount || 0);
      const finalPrice = Math.max(0, basePrice - (basePrice * discount) / 100);
      additions.push({
        serviceId: service.id || null,
        scope: selectedScope,
        cat: service.category || "Altele",
        category: service.category || "Altele",
        name: service.name,
        basePrice,
        discount,
        finalPrice,
        commissionTag: resolveCommissionTag(service),
        done: false,
      });
    });
    if (!additions.length) return;
    setSelectedTreatments((prev) => [...prev, ...additions]);
  };

  const selectionComplete =
    selectedScope === TREATMENT_SCOPES.TOOTH ? Boolean(selectedTooth) : true;
  const selectedLabel = getScopeLabel(selectedScope, selectedTooth);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="sm:col-span-2">
          <h3 className="font-semibold mb-2 text-sm sm:text-base">Alege zona</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { value: TREATMENT_SCOPES.FULL, label: "Globala" },
              { value: TREATMENT_SCOPES.ARCH, label: "Arcada" },
              { value: TREATMENT_SCOPES.TOOTH, label: "Dinte" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`px-3 py-2 rounded border text-sm ${
                  selectedScope === option.value
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                onClick={() => {
                  setSelectedScope(option.value);
                  setSelectedTooth("");
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        {selectedScope === TREATMENT_SCOPES.TOOTH && (
          <>
            <div>
              <h3 className="font-semibold mb-2 text-sm sm:text-base">Arcada superioara</h3>
              <select
                onChange={(e) => setSelectedTooth(e.target.value)}
                className="w-full border rounded p-2 text-sm sm:text-base"
                value={selectedTooth}
              >
                <option value="">Alege dinte</option>
                {upperTeeth.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-sm sm:text-base">Arcada inferioara</h3>
              <select
                onChange={(e) => setSelectedTooth(e.target.value)}
                className="w-full border rounded p-2 text-sm sm:text-base"
                value={selectedTooth}
              >
                <option value="">Alege dinte</option>
                {lowerTeeth.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {selectionComplete && (
        <div className="mt-6">
          <h3 className="font-semibold mb-3 text-base sm:text-lg">
            Tratamente pentru {selectedLabel}
          </h3>

          {activePresets.length > 0 && (
            <div className="mb-4 border rounded p-3 bg-gray-50">
              <p className="font-semibold text-sm mb-2">Preseturi rapide</p>
              <div className="flex flex-wrap gap-2">
                {activePresets.map((preset) => (
                  <Button
                    key={preset.id}
                    variant="secondary"
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {servicesLoading && (
            <p className="text-sm text-gray-500">Se încarcă serviciile...</p>
          )}
          {servicesError && (
            <p className="text-sm text-red-600">
              Nu am putut încărca serviciile. {servicesError}
            </p>
          )}
          {!servicesLoading && !servicesError && categories.length === 0 && (
            <p className="text-sm text-gray-500">
              Nu există servicii configurate. Adminul trebuie să adauge servicii și prețuri.
            </p>
          )}
          {categories.map(([cat, items]) => {
            const categoryColor = categoryColors?.[cat];
            const wrapperStyle = categoryColor
              ? { borderColor: categoryColor, backgroundColor: rgbaFromHex(categoryColor, 0.04) }
              : undefined;
            const titleStyle = categoryColor ? { color: categoryColor } : undefined;
            return (
              <div key={cat} className="border rounded p-3 mb-3" style={wrapperStyle}>
                <h4 className="font-bold mb-2 text-sm sm:text-base" style={titleStyle}>
                  {cat}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {items.map((item) => (
                    <Button
                      key={item.id || item.name}
                      variant={
                        selectedTreatments.some((x) => {
                          if (x.serviceId && item.id) return x.serviceId === item.id;
                          return x.cat === cat && x.name === item.name;
                        })
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() => handleToggleTreatment(cat, item)}
                    >
                      {item.name}
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}

          {selectedTreatments.length > 0 && (
            <div className="mt-6">
              <div className="overflow-x-auto">
                <table className="w-full border text-xs sm:text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="p-2 text-left">Serviciu</th>
                      <th className="p-2 text-left">Preț listă</th>
                      <th className="p-2 text-left">Discount %</th>
                      <th className="p-2 text-left">Preț final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTreatments.map((t, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{t.name}</td>
                        <td className="p-2">{t.basePrice}</td>
                        <td className="p-2">
                          <input
                            type="number"
                            value={t.discount}
                            min="0"
                            onFocus={(e) => e.target.select()}
                            max="100"
                            onChange={(e) => updateDiscount(i, Number(e.target.value))}
                            className="w-16 border rounded p-1 text-xs sm:text-sm"
                          />
                        </td>
                        <td className="p-2">{t.finalPrice}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-right font-bold mt-3 text-sm sm:text-base">
                Total: {total} Lei
              </p>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <Button variant="primary" onClick={handleSaveTooth}>
              {editingTratamentId ? "💾 Salvează modificările" : "✏️ Modifică planul"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedTooth("");
                setSelectedScope(TREATMENT_SCOPES.TOOTH);
                setSelectedTreatments([]);
                setEditingTratamentId(null);
                if (clearEditing) clearEditing();
              }}
            >
              ⬅️ Înapoi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
